import styles from './index.module.css'
import React from 'react'
import type { Playlist } from 'types/playlist'
import { PLAYLIST } from '@disco/constants/ItemTypes'
import { formatTimestamp, formatTimestampShort } from '@disco/utils/time-utils'
import ColorIndicators from 'components/common/color-indicators'
import PlaylistShowcaseArtwork from 'components/common/playlist-showcase-artwork'
import WatermarkingStatusFlag from 'components/explorer/watermarking-status-flag'
import Flex from 'components/ui/flex'
import Lozenge from 'components/ui/lozenge'
import MediaItem from 'components/ui/media-item'
import Skeleton from 'components/ui/skeleton'
import TextOverflow from 'components/ui/text-overflow'
import { E2Avatar } from 'pages/e2/e2.prototype'
import { getOwner } from './utils'
type Props = {
  hideWatermarkingFlag?: boolean
  playlist: Playlist | null | undefined
  showcase?: boolean
  libraryGroupType?: string
}

/**
 * Playlist information display component with owner, timestamp, and status indicators
 * Shows playlist details with color indicators, client version badges, and watermarking status
 * @param hideWatermarkingFlag - Whether to hide watermarking status indicators
 * @param playlist - Playlist data to display, null shows skeleton loading
 * @param showcase - Whether to display showcase artwork
 * @param libraryGroupType - Type of library group for owner display logic
 */
const PlaylistInfo = ({
  hideWatermarkingFlag,
  playlist,
  showcase,
  libraryGroupType,
}: Props) => {
  if (!playlist) {
    return (
      <MediaItem
        media={showcase ? <PlaylistShowcaseArtwork /> : null}
        title={<Skeleton />}
        caption={<Skeleton />}
      />
    )
  }

  const {
    is_client_version,
    name,
    owner,
    playlist_tags,
    timestamp,
    watermarking_status,
  } = playlist

  const renderOwner = () => {
    const libraryOwner = getOwner(playlist, libraryGroupType)
    return libraryOwner ? (
      <div className={styles.owner}>
        <TextOverflow content={libraryOwner} />
      </div>
    ) : owner?.member_of_business ? (
      <E2Avatar
        firstName={owner.first_name}
        lastName={owner.last_name}
        email={owner.email}
      />
    ) : null
  }

  return (
    <MediaItem
      media={showcase ? <PlaylistShowcaseArtwork /> : null}
      title={name || 'Untitled playlist'}
      caption={
        <Flex spacing="small">
          {owner && renderOwner()}
          <Flex.Content>
            <div title={formatTimestamp(timestamp)}>
              {formatTimestampShort(timestamp)}
            </div>
          </Flex.Content>
          {is_client_version && <Lozenge text="Client" />}
          {playlist_tags && <ColorIndicators tags={playlist_tags} />}
          {!hideWatermarkingFlag && watermarking_status && (
            <WatermarkingStatusFlag
              short
              type={PLAYLIST}
              watermarking_status={watermarking_status}
            />
          )}
        </Flex>
      }
    />
  )
}

export default React.memo<Props>(PlaylistInfo)
